import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import ReactSVG from "react-svg";
import { throttle, isEmpty, get } from "lodash";
import cx from "classnames";
import { getFormValues, getFormSyncErrors } from "redux-form";

import baseService from "services/BaseService";
import Loader from "components/Loader";
import DynamicForm from "../../../pages/Ordering/common/DynamicForm";

import { getGridsStorageFields, setGridFieldsToStorage } from "helpers";

const NOTIFICATION_SETTINGS_GRID_KEY = "user_profile_modal_notification_settings_cells_width";

const OBJECT_TYPE = "TASK";

const RESIZE_TIMEOUT = 10;

const HEADERS = ["Повідомлення", "Автор", "Відповідальний"];
const HEADERS_DEFAULT_WIDTH = [227, 82, 150];
const MINIMUM_RESIZE_CELL_WIDTH = 50;
const TYPES_FORM_NAME = "notification-settings-types";

const CheckBox = props => {
    const { isChecked, onChange, disabled } = props;
    function handleCheck(e) {
        if (onChange && typeof onChange === "function") {
            onChange(e.target.checked);
        }
    }
    return (
        <label className="container grid-checkbox">
            <input type="checkbox" checked={isChecked} onChange={handleCheck} />

            <div className={cx("check-mark", { "disabled": disabled })}>
                <ReactSVG className="checkbox__unchecked-icon" src={`/data/svg/checkbox_unchecked.svg`} />
                <ReactSVG className="checkbox__checked-icon" src={`/data/svg/checkbox_checked.svg`} />
            </div>
        </label>
    );
};

const mapStateToProps = state => ({
    typeValues: getFormValues(TYPES_FORM_NAME)(state),
    typeErrors: getFormSyncErrors(TYPES_FORM_NAME)(state),
});

const NotificationSettings = props => {
    const { t, typeValues, typeErrors, canEditNotificationSubscriptions, isOrderNotifications, orderId } = props;

    const [notificationSettingsTypes, setNotificationSettingsTypes] = useState(null);
    const [notificationSettingsActions, setNotificationSettingsActions] = useState(null);
    const [notificationSettingsMatrix, setNotificationSettingsMatrix] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isResizing, setIsResizing] = useState(false);

    let isDataReady = false;
    if (isOrderNotifications) {
        // we do not need notificationSettingsTypes for orderNotifications
        isDataReady = notificationSettingsActions && notificationSettingsMatrix;
    } else {
        isDataReady = notificationSettingsTypes && notificationSettingsActions && notificationSettingsMatrix;
    }

    const [cellsWidth, setCellsWidth] = useState(
        getGridsStorageFields(NOTIFICATION_SETTINGS_GRID_KEY) || HEADERS_DEFAULT_WIDTH
    ); // message, author, responsible
    const resizeRefs = useRef([]); // message, author, responsible

    const settingsTypesDynData = useMemo(() => {
        if (!notificationSettingsMatrix || !notificationSettingsTypes || !Array.isArray(notificationSettingsTypes)) {
            return [];
        }
        return [
            {
                view: "form",
                widgets: [
                    {
                        title: "Типи Заявок",
                        key: "types",
                        savedValues: notificationSettingsMatrix.orderTypes,
                        values: notificationSettingsTypes.map(type => ({
                            value: type.templateName,
                            key: type.templateId,
                        })),
                        isReq: false,
                        widgetType: "multiselect",
                        disabled: !canEditNotificationSubscriptions,
                    },
                ],
            },
        ];
    }, [notificationSettingsTypes, notificationSettingsMatrix]);

    const requestNotificationSettingsTypes = () => {
        setIsLoading(true);
        return baseService
            .get("get_user_notification_settings_types")
            .then(({ success, result }) => {
                setIsLoading(false);
                if (success) {
                    if (result) {
                        setNotificationSettingsTypes(result);
                    }
                } else {
                    console.error("NotificationSettings::requestNotificationSettingsTypes: Failed.");
                }
            })
            .catch(e => {
                console.error("NotificationSettings::requestNotificationSettingsTypes: Error ", e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const requestNotificationSettingsActions = () => {
        setIsLoading(true);
        return baseService
            .get("get_user_notification_settings_actions", { data: { objectType: OBJECT_TYPE } })
            .then(({ success, result }) => {
                setIsLoading(false);
                if (success) {
                    if (result) {
                        setNotificationSettingsActions(result);
                    }
                } else {
                    console.error("NotificationSettings::requestNotificationSettingsActions: Failed.");
                }
            })
            .catch(e => {
                console.error("NotificationSettings::requestNotificationSettingsActions: Error ", e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    const requestNotificationSettingsMatrix = () => {
        // const key = "get_user_notification_settings_matrix";
        const key = isOrderNotifications
            ? "get_user_notification_settings_matrix_card"
            : "get_user_notification_settings_matrix";
        const data = { objectType: OBJECT_TYPE };
        if (isOrderNotifications) {
            data.objectId = orderId;
        }
        setIsLoading(true);
        return baseService
            .get(key, { data })
            .then(({ success, result }) => {
                setIsLoading(false);
                if (success) {
                    if (result) {
                        // somehow get_user_notification_settings_matrix_card does not return owner/creator params..
                        setNotificationSettingsMatrix(result);
                    }
                } else {
                    console.error("NotificationSettings::requestNotificationSettingsMatrix: Failed.");
                }
            })
            .catch(e => {
                console.error("NotificationSettings::requestNotificationSettingsMatrix: Error ", e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const requestNotificationSettingsData = async () => {
        setIsLoading(true);
        try {
            const queries = [requestNotificationSettingsActions, requestNotificationSettingsMatrix];
            if (!isOrderNotifications) {
                queries.push(requestNotificationSettingsTypes);
            }
            await Promise.all(queries.map(fn => fn()));
        } catch (e) {
            console.error("NotificationSettings::requestNotificationSettingsData: Error: ", e);
        } finally {
            setIsLoading(false);
        }
    };

    const moveResizeRef = useRef(null);
    const finishResizeRef = useRef(null);

    const handleResize = (event, cellIndex) => {
        setIsResizing(true);
        const cellResizer = resizeRefs.current[cellIndex];
        const resizeState = {
            resizedColumnIndex: cellIndex,
            resizeStartX: event.pageX,
            defaultCellWidth: cellResizer?.parentElement?.offsetWidth,
        };

        const handleMoveResize = throttle(event => {
            const { resizeStartX, defaultCellWidth, resizedColumnIndex } = resizeState;
            if (!defaultCellWidth) return;

            const newWidth = defaultCellWidth + event.pageX - resizeStartX;
            const finalNewWidth = newWidth <= MINIMUM_RESIZE_CELL_WIDTH ? MINIMUM_RESIZE_CELL_WIDTH : newWidth;
            setCellsWidth(prev => prev.map((width, index) => (index === resizedColumnIndex ? finalNewWidth : width)));
        }, RESIZE_TIMEOUT);

        const handleFinishResize = event => {
            setIsResizing(false);
            // Remove event listeners
            document.removeEventListener("mousemove", moveResizeRef.current);
            document.removeEventListener("mouseup", finishResizeRef.current);
            document.removeEventListener("mouseleave", finishResizeRef.current);
        };

        // Save stable references
        moveResizeRef.current = handleMoveResize;
        finishResizeRef.current = handleFinishResize;

        // Attach event listeners
        document.addEventListener("mousemove", moveResizeRef.current, { passive: true });
        document.addEventListener("mouseup", finishResizeRef.current, { passive: true });
        document.addEventListener("mouseleave", finishResizeRef.current, { passive: true });
    };

    function handleCheck(key, ruleSetId, isChecked) {
        setNotificationSettingsMatrix(prev => {
            const prevData = prev[key] || [];
            return {
                ...prev,
                [key]: isChecked ? [...prevData, ruleSetId] : prevData.filter(key => key !== ruleSetId),
            };
        });
    }

    async function handleSave() {
        const ownerSets = get(notificationSettingsMatrix, "owner", []);
        const creatorSets = get(notificationSettingsMatrix, "creator", []);
        const orderTypes = get(typeValues, "types", []);

        const data = {
            objectType: OBJECT_TYPE,
            notificationRole: [
                {
                    key: "owner",
                    ruleSetId: ownerSets,
                },
                {
                    key: "creator",
                    ruleSetId: creatorSets,
                },
            ],
            orderTypes,
            orderTypesAll: orderTypes.length === 0,
        };

        if (isOrderNotifications && orderId) {
            delete data.orderTypes;
            data.orderTypesAll = false;
            data.objectId = orderId;
        }

        try {
            setIsLoading(true);
            const params = {
                data,
                jsonType: true,
            };
            await baseService.post("save_user_notification_settings", params);
        } catch (e) {
            console.error("NotificationSettings::handleSave: Error ", e);
        } finally {
            setIsLoading(false);
        }
    }

    const renderHeaderCell = (cellName, index) => {
        return (
            <div className="notification-settings-table-header__cell" style={{ width: `${cellsWidth[index]}px` }}>
                <div className="notification-settings-table-header__cell-name">{cellName}</div>
                <div
                    className="notification-settings-table-header__cell-resizer"
                    onMouseDown={event => handleResize(event, index)}
                    ref={el => (resizeRefs.current[index] = el)}
                ></div>
            </div>
        );
    };

    const renderRow = (row, index) => {
        const { name, ruleSetId } = row;
        return (
            <div className="notification-settings__row" key={index}>
                <div className="notification-settings__row-cell" style={{ width: `${cellsWidth[0]}px` }}>
                    <div className="notification-settings__row-cell-name">{name}</div>
                </div>
                {/* Author / creator key */}
                <div className="notification-settings__row-cell" style={{ width: `${cellsWidth[1]}px` }}>
                    <CheckBox
                        isChecked={notificationSettingsMatrix.creator?.includes(ruleSetId)}
                        onChange={isChecked => handleCheck("creator", ruleSetId, isChecked)}
                        disabled={!canEditNotificationSubscriptions}
                    />
                </div>
                {/* Responsible / owner key */}
                <div className="notification-settings__row-cell" style={{ width: `${cellsWidth[2]}px` }}>
                    <CheckBox
                        isChecked={notificationSettingsMatrix.owner?.includes(ruleSetId)}
                        onChange={isChecked => handleCheck("owner", ruleSetId, isChecked)}
                        disabled={!canEditNotificationSubscriptions}
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!isResizing) {
            setGridFieldsToStorage(NOTIFICATION_SETTINGS_GRID_KEY, cellsWidth);
        }
    }, [isResizing]);

    useEffect(() => {
        requestNotificationSettingsData();
    }, []);
    return (
        <>
            {isLoading && <Loader />}
            <div className="notification-settings-wrapper">
                {isDataReady && (
                    <>
                        <div className="notification-settings-central-wrapper">
                            <div className="notification-settings-wrapper__title">{t("getNotificationsByEvents")}</div>
                            <div className={cx("notification-settings-table-scroll-wrapper", { 'without-form': isOrderNotifications })}>
                                <div className="notification-settings-table-header">
                                    {HEADERS.map(renderHeaderCell)}
                                </div>
                                <div className="notification-settings-table">
                                    {notificationSettingsActions && notificationSettingsActions.map(renderRow)}
                                </div>
                            </div>
                            {!isOrderNotifications && <DynamicForm
                                className="notification-settings-types-form"
                                formName={TYPES_FORM_NAME}
                                dynData={settingsTypesDynData}
                                currentId={null}
                                uploadFileSource="form"
                                portalPlacement="right"
                                t={t}
                            />}
                        </div>
                        <div className="notification-settings-footer">
                            <button
                                title={t("save")}
                                className={cx(
                                    "notification-settings-footer__footer-button notification-settings-footer__footer-button--save",
                                    {
                                        disabled:
                                            isLoading || !isEmpty(typeErrors) || !canEditNotificationSubscriptions,
                                    }
                                )}
                                type="button"
                                onClick={handleSave}
                            >
                                <ReactSVG src={`/data/svg/input_save.svg`} />
                                <span>{t("save")}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default connect(mapStateToProps, null)(NotificationSettings);
